/**
 * The wire services an education payment can go through, worked out from
 * the purpose list rather than written down.
 *
 * Each service is its own purpose, twice over — with a loan and without —
 * and the screens used to hold those ids inline. They were Xano's, and ours
 * are different, so the dashboard quote asked for purposes that do not
 * exist here and the calculator answered nothing at all.
 */

/** How each mode is written on screen; the data spells them without spaces. */
const MODE_LABEL: Record<string, string> = {
  flywire: "Flywire",
  westernunion: "Western Union",
  cibc: "CIBC",
  paymytuition: "PayMyTuition",
  viatransfer: "Via Transfer",
  gic: "GIC",
};

/** The five services an education payment is sent through. */
const WIRE_MODES = [
  "viatransfer",
  "westernunion",
  "paymytuition",
  "flywire",
  "cibc",
];

export type PurposeRow = {
  id: string | number;
  name?: string;
  mode?: string | null;
  loan?: boolean;
  isLoan?: boolean;
};

export type ModeOption = { label: string; value: string; loan: boolean };

/** Every education purpose, labelled by its service, both loan variants. */
export function educationPurposeMap(purposes: PurposeRow[]): ModeOption[] {
  return purposes
    .map((p) => {
      const mode = String(p.mode ?? "").toLowerCase();
      const label = MODE_LABEL[mode];
      if (!label) return null;
      return {
        label,
        value: String(p.id),
        loan: Boolean(p.loan ?? p.isLoan),
      };
    })
    .filter((option): option is ModeOption => option !== null);
}

/**
 * The radio list of services. One entry per service, the no-loan variant,
 * since choosing a loan afterwards swaps it for the matching one.
 */
export function educationModeOptions(purposes: PurposeRow[]): ModeOption[] {
  const all = educationPurposeMap(purposes);
  return WIRE_MODES.map((mode) => {
    const label = MODE_LABEL[mode];
    return all.find((o) => o.label === label && !o.loan);
  }).filter((option): option is ModeOption => option !== undefined);
}
